import { useGameStore } from '@/stores/gameStore';
import { Team } from '@/types/player';
import './LifePointsDisplay.css';

// チームごとの最大ライフポイント
const MAX_LIFE_POINTS = {
  [Team.TEAM_1]: 60, // デュオ側
  [Team.TEAM_2]: 80, // ソロ側
};

/**
 * ライフポイントの色を取得
 */
const getHealthColor = (percentage: number): string => {
  if (percentage > 0.6) return '#4ade80'; // 緑
  if (percentage > 0.3) return '#fbbf24'; // 黄/オレンジ
  return '#ef4444'; // 赤
};

/**
 * 持ち点表示コンポーネント
 * 各プレイヤーの持ち点とラウンド番号をプログレスバーで表示
 */
export const LifePointsDisplay = () => {
  const { gameState, matchState } = useGameStore();

  if (!gameState || !matchState) {
    return null;
  }

  const { players } = gameState;
  const { lifePoints, roundNumber } = matchState;

  // Team 1のプレイヤー（2人）
  const team1Players = players.filter(p => p.team === Team.TEAM_1);
  // Team 2のプレイヤー（1人）
  const team2Player = players.find(p => p.team === Team.TEAM_2);

  /**
   * プレイヤーのライフバーをレンダリング
   */
  const renderPlayerLife = (
    playerId: string,
    playerName: string,
    team: Team
  ) => {
    const points = lifePoints[playerId] ?? 0;
    const maxPoints = MAX_LIFE_POINTS[team];
    const percentage = Math.max(0, Math.min(1, points / maxPoints));
    const isLow = percentage <= 0.33;
    const isCritical = percentage <= 0.17;

    return (
      <div
        key={playerId}
        className={`player-life ${isLow ? 'low' : ''} ${isCritical ? 'critical' : ''}`}
      >
        <div className="player-life__header">
          <span className="player-name">{playerName}</span>
          <span className="player-points">{points}</span>
        </div>
        <div
          className="life-bar"
          role="progressbar"
          aria-valuenow={points}
          aria-valuemin={0}
          aria-valuemax={maxPoints}
          aria-label={`${playerName}のライフポイント: ${points}/${maxPoints}`}
        >
          <div
            className="life-bar__fill"
            style={{
              width: `${percentage * 100}%`,
              backgroundColor: getHealthColor(percentage),
            }}
          />
        </div>
      </div>
    );
  };

  return (
    <div className="life-points-display">
      <div className="life-points-header">
        <span className="round-indicator">Round {roundNumber}</span>
      </div>

      <div className="life-points-container">
        {/* Team 1 - グループ化して強調 */}
        <div className="team-section team-1">
          <div className="team-label">
            <span className="team-badge team-badge--duo">DUO</span>
            Team 1
            <span className="team-max">({MAX_LIFE_POINTS[Team.TEAM_1]}pts)</span>
          </div>
          <div className="players-list players-list--duo">
            {team1Players.map(player =>
              renderPlayerLife(player.id, player.name, Team.TEAM_1)
            )}
          </div>
        </div>

        <div className="vs-divider">VS</div>

        {/* Team 2 - ソロ表示 */}
        <div className="team-section team-2">
          <div className="team-label">
            <span className="team-badge team-badge--solo">SOLO</span>
            Team 2
            <span className="team-max">({MAX_LIFE_POINTS[Team.TEAM_2]}pts)</span>
          </div>
          {team2Player && (
            <div className="players-list">
              {renderPlayerLife(team2Player.id, team2Player.name, Team.TEAM_2)}
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default LifePointsDisplay;
